/**
 * DMPage — Direct Messaging with real-time WebSocket
 * Route: /messages
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Send, Image, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(d) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const DMPage = () => {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const token = localStorage.getItem('fq_auth_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/dm/conversations`, { credentials: 'include', headers })
      .then(r => r.json())
      .then(d => setConvos(d.conversations || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const loadMessages = useCallback(async (otherId) => {
    setMsgLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dm/messages/${otherId}`, { credentials: 'include', headers });
      const d = await res.json();
      setMessages(d.messages || []);
    } catch {} finally { setMsgLoading(false); }
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadMessages(selected.other_user?.user_id);
  }, [selected, loadMessages]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // WebSocket for real-time
  useEffect(() => {
    if (!user) return;
    try {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/ws?user_id=${user.user_id}`);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'dm' && data.message) {
            setMessages(prev => [...prev, data.message]);
          }
        } catch {}
      };
      wsRef.current = ws;
      return () => ws.close();
    } catch {}
  }, [user]);

  const handleSend = async () => {
    if (!text.trim() || !selected || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/dm/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        credentials: 'include',
        body: JSON.stringify({ to_user_id: selected.other_user?.user_id, text: text.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setText('');
      }
    } catch {} finally { setSending(false); }
  };

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="text-center"><p className="text-gray-500 mb-3">Sign in to message</p><Link to="/profile" className="text-[#FF6B35] font-semibold">Sign In</Link></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-0">
      <div className="flex h-[calc(100vh-56px)] md:h-screen max-w-5xl mx-auto">
        {/* Conversation list */}
        <div className={`${selected ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 bg-white border-r border-gray-100`}>
          <div className="p-4 border-b border-gray-100">
            <h1 className="text-xl font-bold text-gray-900 mb-3">Messages</h1>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search conversations..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/30" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? [1,2,3].map(i => <div key={i} className="p-4 animate-pulse"><div className="flex gap-3"><div className="w-10 h-10 bg-gray-100 rounded-full" /><div className="flex-1 space-y-2"><div className="h-3 bg-gray-100 rounded w-1/2" /><div className="h-3 bg-gray-100 rounded w-3/4" /></div></div></div>) :
            convos.filter(c => !searchQ || c.other_user?.name?.toLowerCase().includes(searchQ.toLowerCase())).map(c => (
              <button key={c.room} onClick={() => setSelected(c)}
                className={`w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors ${selected?.room === c.room ? 'bg-[#FFF5F0]' : ''}`}>
                <Avatar className="w-10 h-10 flex-shrink-0">
                  <AvatarImage src={c.other_user?.picture} />
                  <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-xs font-bold">{(c.other_user?.name || 'F')[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-900 truncate">{c.other_user?.name}</span>
                    <span className="text-[10px] text-gray-400">{timeAgo(c.last_message_at)}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{c.last_message}</p>
                </div>
                {c.unread > 0 && <span className="w-5 h-5 bg-[#FF6B35] text-white text-[10px] font-bold rounded-full flex items-center justify-center">{c.unread}</span>}
              </button>
            ))}
            {!loading && convos.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No conversations yet</p>}
          </div>
        </div>

        {/* Chat area */}
        <div className={`${selected ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-white`}>
          {selected ? (
            <>
              <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                <button onClick={() => setSelected(null)} className="md:hidden p-1"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
                <Avatar className="w-8 h-8"><AvatarImage src={selected.other_user?.picture} /><AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-xs">{(selected.other_user?.name || 'F')[0]}</AvatarFallback></Avatar>
                <span className="font-semibold text-gray-900 text-sm">{selected.other_user?.name}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {msgLoading ? <div className="text-center text-gray-400 text-sm py-8">Loading...</div> :
                messages.map(m => (
                  <div key={m.message_id} className={`flex ${m.from_user_id === user.user_id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                      m.from_user_id === user.user_id ? 'bg-[#FF6B35] text-white rounded-br-md' : 'bg-gray-100 text-gray-900 rounded-bl-md'
                    }`}>
                      {m.text}
                      <span className={`block text-[9px] mt-1 ${m.from_user_id === user.user_id ? 'text-white/60' : 'text-gray-400'}`}>{timeAgo(m.created_at)}</span>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-3 border-t border-gray-100 flex gap-2">
                <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
                  placeholder="Type a message..." className="flex-1 px-4 py-2.5 bg-gray-50 rounded-2xl text-sm focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/30" />
                <button onClick={handleSend} disabled={!text.trim() || sending}
                  className="w-10 h-10 bg-[#FF6B35] text-white rounded-2xl flex items-center justify-center disabled:opacity-50">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DMPage;
